/**
 * ArrangeableBorderContainer subclasses BorderContainer to provide additional management of children added to the sides and bottom.
 * It allows multiple children on either or both sides as well as on the bottom.  Children on the sides are placed in
 * AccordionContainers.  Children on the bottom are placed in a TabContainer.  Children can be moved among borders via
 * drag and drop.  The collection of children on a border can be "collapsed" resulting in a row or column of buttons
 * along the border.  These buttons can then be used to temporarily expand individual children on top of the center
 * content.
 *
 * Some aspects of the ArrangeableBorderContainer's behavior are governed by client settable properties.  These properties may be
 * fixed, meaning that they should be set at construction time and not changed, or dynamic, meaning that they can be
 * changed at any time.  Since the ArrangeableBorderContainer inherits the "Stateful" mixin, get, set and watch methods can be used
 * to manage and track dynamic properties.
 *
 * @module MW/layout/ArrangeableBorderContainer
 * @copyright Copyright 2017 The MathWorks, Inc.
 */
define([
    "dojo/_base/declare",
    "dojo/_base/event",
    "dojo/_base/lang",
    "dojo/has",
    "dojo/mouse",
    "dojo/on",
    "dojo/Stateful",
    "dojo/touch",
    "dojo/dom-attr",
    "dojo/dom-class",
    "dojo/dom-geometry",
    "dojo/dom-style",
    "dijit/registry",
    "dijit/layout/ContentPane",
    "MW/layout/BorderContainer",
    "MW/layout/BottomContainer",
    "MW/layout/Geometry",
    "MW/layout/LayoutChildProperty",
    "MW/layout/LayoutContainerEvent",
    "MW/layout/LayoutUtils",
    "MW/layout/MaximizingContainer",
    "MW/layout/PanelContainer",
    "MW/layout/PanelDragger",
    "MW/layout/Restorable",
    "MW/layout/SelectionManager",
    "MW/layout/SideContainer",
    "MW/layout/StatefulExtension",
    "dojo/i18n!l10n/nls/layout"
], function (declare, event, lang, has, mouse, on, Stateful, touch, domAttr, domClass, domGeometry, domStyle, registry,
             ContentPane, BorderContainer, BottomContainer, Geometry, LayoutChildProperty, LayoutContainerEvent, LayoutUtils,
             MaximizingContainer, PanelContainer, PanelDragger, Restorable, SelectionManager, SideContainer, StatefulExtension,
             layoutL10n) {

    /**
     * @class InternalPanelDragger
     * @classdesc internal class used to manage transfer of panels among container borders
     */
    var InternalPanelDragger = declare([PanelDragger], {

        constructor: function (ArrangeableBorderContainer) {
            this._ArrangeableBorderContainer = ArrangeableBorderContainer;
        },

        _continueDrag: function (e) {
            // See if the user has dragged "close" to a border that lacks a PanelContainer but
            // still within the movable border container.  If so add a container, reusing the container from
            // which the drag originated if appropriate.
            var borderBox = domGeometry.position(this._ArrangeableBorderContainer.domNode);
            borderBox.l = borderBox.x;
            borderBox.t = borderBox.y;
            var region, container, topHeight, leftThreshold, rightThreshold, bottomThreshold;
            if (this._ArrangeableBorderContainer.getChildInRegion("top")) {
                topHeight = this._ArrangeableBorderContainer.getFreeDimension("top");
                borderBox.t += topHeight;
                borderBox.h -= topHeight;
            }
            if (e.pageX >= borderBox.l && e.pageX <= (borderBox.l + borderBox.w) &&
                e.pageY >= borderBox.t && e.pageY <= (borderBox.t + borderBox.h))
            {
                leftThreshold = rightThreshold = borderBox.w / 8;
                bottomThreshold = borderBox.h / 8;
                // When the last panel is dragged out of a container, the container is removed and shouldn't reappear
                // unless the panel is dragged back within the original container bounds.
                switch (this._originInfo.region) {
                    case "left":
                        leftThreshold = Math.min(leftThreshold, this._originInfo.containerSize.w);
                        break;
                    case "right":
                        rightThreshold = Math.min(rightThreshold, this._originInfo.containerSize.w);
                        break;
                    case "bottom":
                        bottomThreshold = Math.min(bottomThreshold, this._originInfo.containerSize.h);
                }
                if (!this._ArrangeableBorderContainer._getPanelContainerInRegion("left") && e.pageX < (borderBox.l + leftThreshold)) {
                    region = "left";
                } else if (!this._ArrangeableBorderContainer._getPanelContainerInRegion("right") && e.pageX > (borderBox.l + borderBox.w - rightThreshold)) {
                    region = "right";
                } else if (!this._ArrangeableBorderContainer._getPanelContainerInRegion("bottom") && e.pageY > (borderBox.t + borderBox.h - bottomThreshold) &&
                           e.pageX > (borderBox.l + this._ArrangeableBorderContainer.getFreeDimension("left")) &&
                           e.pageX < (borderBox.l + borderBox.w - this._ArrangeableBorderContainer.getFreeDimension("right"))) {
                    region = "bottom";
                }
            }
            if (region && this._isDragInPermitted(this._originInfo, region)) {
                if (this._originInfo.region === region) {
                    // The panel is being dragged back into its original container
                    container = this._originInfo.container;
                }
                if (!container) {
                    container = this._ArrangeableBorderContainer._createPanelContainer(region);
                }
                this._ArrangeableBorderContainer._addPanelContainer(container, region, this._originInfo.widget);
            }
            this.inherited(arguments);
        },

        _endDrag: function () {
            var widget = this._originInfo.widget;
            this.inherited(arguments);
            var panelContainer = this._ArrangeableBorderContainer._getPanelContainerAncestor(widget);
            if (widget.properties && panelContainer) {
                widget.properties.set(LayoutChildProperty.REGION, panelContainer.region);
            }
        },

        _positionPanel: function (box) {
            // Limit to container bounds
            var mainBox = domGeometry.position(this._ArrangeableBorderContainer.domNode);
            var bottomLimit, rightLimit;
            if (box.t < mainBox.y) {
                box.t = mainBox.y;
            } else {
                bottomLimit = mainBox.y + mainBox.h - 20;
                if (box.t > bottomLimit) {
                    box.t = bottomLimit;
                }
            }
            if (box.l < mainBox.x) {
                box.l = mainBox.x;
            } else {
                rightLimit = mainBox.x + mainBox.w - 20;
                if (box.l > rightLimit) {
                    box.l = rightLimit;
                }
            }
            this.inherited(arguments);
        },

        _clearOriginInfo: function () {
            if (this._originInfo.container.isEmpty()) {
                this._ArrangeableBorderContainer._destroyPanelContainer(this._originInfo.container);
            }
            this.inherited(arguments);
        },

        _isDragInPermitted: function(originInfo, toRegion) {
            // Disallow drag to "bottom" if it is occupying the entire center
            if (toRegion === "bottom" && !this._ArrangeableBorderContainer._hasCenterChild()) {
                return false;
            }
            return this.inherited(arguments);
        }
    });

    /**
     * Properties supported via the Stateful API
     */
    var IS_LEFT_COLLAPSED   = "isLeftCollapsed";
    var IS_RIGHT_COLLAPSED  = "isRightCollapsed";
    var IS_BOTTOM_COLLAPSED = "isBottomCollapsed";

    var COLLAPSED_PROPERTY = {
        left: IS_LEFT_COLLAPSED,
        right: IS_RIGHT_COLLAPSED,
        bottom: IS_BOTTOM_COLLAPSED
    };

    var LEFT_WIDTH = "leftWidth";
    var RIGHT_WIDTH = "rightWidth";
    var BOTTOM_HEIGHT = "bottomHeight";

    var DIMENSION_PROPERTY = {
        left: LEFT_WIDTH,
        right: RIGHT_WIDTH,
        bottom: BOTTOM_HEIGHT
    };

    /**
     * @class MW/layout/ArrangeableBorderContainer
     * @classdesc A user rearrangeable BorderContainer
     */
    var ArrangeableBorderContainer = declare([BorderContainer, StatefulExtension], {
        /**
         * @property {boolean} enableExpandOnTop - true if it should be possible to expand individual children on top of
         *                                         the center content following collapse of the hosting border region
         * @property (Object)   borderOptions    - Specifies collective behavior options for the set of children on each border.
         *                                         May include entries for any of "left", "right" and "bottom", where
         *                                         each entry is an object containing any of the following:
         *                                         isCollapsible - true if it should be possible to collapse the set of children occupying the border (default is true)
         *                                         isProportional - true if the children occupying the border should be resized proportionally when the overall container size changes (default is true)
         *                                         isResizable - true if the user should be able to adjust the size of the children occupying the border (default is true)
         *                                         minSize - Size in pixels to which border children may be reduced without collapsing (default is 120)
         * @property {boolean} supportFooter     - true if the container should be configured to support a footer below the center
         * @instance
         */
        FixedProperties: undefined,
        // The following properties can be specified at construction time but subsequent changes will be ignored

        design: "sidebar",
        enableOnTopExpand: true,
        supportFooter: true,

        /**
         * @property {boolean} isLeftCollapsed - Reflects/controls the collapsed state of the children on the left
         * @property {boolean} isRightCollapsed - Reflects/controls the collapsed state of the children on the right
         * @property {boolean} isBottomCollapsed - Reflects/controls the collapsed state of the children on the bottom
         * @property {Number} leftWidth - Reports/controls the width of the children on the left.  If >= 1 pixels assumed.
         *                                If < 1 interpreted as a fraction of the container width.
         * @property {Number} rightWidth - Reports/controls the width of the children on the right.  If >= 1 pixels assumed.
         *                                If < 1 interpreted as a fraction of the container width.
         * @property {Number} bottomHeight - Reports/controls the height of the children on the bottom.  If >= 1 pixels assumed.
         *                                If < 1 interpreted as a fraction of the container height.
         * @instance
         */
        DynamicProperties: undefined,

        constructor: function () {
            this._panelDragger = new InternalPanelDragger(this);
            this._pendingPanelIdToRegionMap = {};
            this._requestedDimension = {};
        },

        postMixInProperties: function () {
            this.inherited(arguments);
            if (!this.selectionManager) {
                this.selectionManager = new SelectionManager();
            }
            this.selectionManager.registerContainer(this);
        },

        buildRendering: function () {
            this.inherited(arguments);
            domClass.add(this.domNode, "movableBorderContainer");
            domClass.add(this.domNode, "mainContainer"); // alias used by tests
        },

        /**
         * Overridden to provide special handling of widgets added to the left, right or bottom
         * The child will be managed in accordance with the provided properties as described below.
         *
         * @param widget {_Widget} the child to add
         * @param properties {Object} an object containing any of the following properties:
         * @param properties.region {string} with value "top", "left", "bottom", "right" or "center"
         *        Determines the placement of the child within the container.  When the region is "top" or "center"
         *        the inherited BorderContainer behavior applies.
         * @param properties.preferredWidth {Number} optional
         * @param properties.preferredHeight {Number} optional
         *
         * @instance
         */
        addWithProperties: function (widget, properties) {
            var region = properties.region;
            if (this._isInternalAdd || region === "center" || region === "top") {
                this.inherited(arguments);
            } else {
                widget.region = region;
                this._bindPanelProperties(widget, properties);
                this.addChild(widget);
            }
        },

        /**
         * Overridden to provide special handling of widgets added to the left, right or bottom.
         * The properties documented for the addWithProperties method apply here as well but will be read
         * directly from the widget.
         *
         * @param widget {_Widget} the child to add
         *
         * @instance
         */
        addChild: function (widget) {
            var region = widget.region;
            var regions, i, container, added, pendingRegion;
            if (this._isInternalAdd || region === "center" || region === "top") {
                this.inherited(arguments);
                this._updateBottomContainer();
                return;
            }
            this._bindPanelProperties(widget, widget);
            if (!widget.properties.panelId) {
                // Grandfather the MOTW approach to defining layout
                widget.properties.panelId = widget.layoutId;
            }
            if (this.isRestoringState()) {
                // Add the child back to its previous location
                regions = ["left", "right", "bottom"];
                for (i = 0; i < regions.length && !added; i += 1) {
                    container = this._getPanelContainerInRegion(regions[i]);
                    if (container && container.isExpecting(widget.properties.panelId)) {
                        this._addToPanelContainer(container, widget);
                        added = true;
                        widget.properties.set(LayoutChildProperty.REGION, regions[i]);
                    }
                }
                if (added) {
                    return;
                } else {
                    this.endStateRestore();
                }
            }
            pendingRegion = this._pendingPanelIdToRegionMap[widget.properties.panelId];
            if (pendingRegion) {
                region = pendingRegion;
                delete this._pendingPanelIdToRegionMap[widget.properties.panelId];
            }
            this._addToBorder(widget, region);
        },

        /**
         * Removes a child from the container
         *
         * @param widget {_Widget} the child to be removed
         *
         * @instance
         */
        removeChild: function (widget) {
            var region = widget.region;
            if (this._isInternalRemove  || region === "center" || region === "top") {
                this.inherited(arguments);
                this._updateBottomContainer();
            } else {
                // Remove widget from owning container
                this._getPanelContainerAncestor(widget).removePanel(widget, widget.properties);
                if (widget._unbindPanelProperties) {
                    widget._unbindPanelProperties();
                }
            }
        },

        /**
         * Removes children from the left, right and bottom

         * @param destroy {boolean} true if the removed children should be destroyed
         *
         * @instance
         */
        removeAllPanels: function (destroy) {
            var that = this;
            ["left", "right", "bottom"].forEach(function (region) {
                var container = that._getPanelContainerInRegion(region);
                if (container) {
                    container.removeAll(destroy);
                    that._removePanelContainer(region);
                }
            });
        },

        setFooter: function (widget) {
            // If there is already a bottom container set the footer on it.
            // Otherwise add the footer to the bottom as a non-collapsible child
            var bottomChild = this.getChildInRegion("bottom");
            if (bottomChild && bottomChild.isInstanceOf(BottomContainer)) {
                bottomChild.setFooter(widget);
            } else if (widget) {
                this._addFooterToBottom(widget);
            } else {
                this._removeFooterFromBottom();
            }
        },

        getFooter: function () {
            var bottomChild = this.getChildInRegion("bottom");
            return bottomChild ? bottomChild.getFooter() : null;
        },

        /**
         * Overrides the inherited method to include the states of each panel container
         *
         * @instance
         */
        getState: function (setId) {
            var state = setId ? {} : this.inherited(arguments);
            var that = this;
            ["left", "right", "bottom"].forEach(function (region) {
                var container = that._getPanelContainerInRegion(region);
                if (container) {
                    if (!state[region]) {
                        state[region] = {};
                    }
                    lang.mixin(state[region], container.getState(setId));
                    if (setId && that._containsOnly(container, setId)) {
                        state[region].border = that._getBorderState(that._borderInfo[region]);
                    }
                }
            });
            return state;
        },

        /**
         * Overrides the inherited method to pass state to the side containers
         *
         * @instance
         */
        setState: function (state, setId, isContextual) {
            var removedChildren, leftOverChildren;
            var that = this;
            var container;
            var containedPanelIds = [];
            removedChildren = [];
            var contentBox;
            this._isInSetState = true;
            ["left", "right", "bottom"].forEach(function (region) {
                container = that._getPanelContainerInRegion(region);
                if (container) {
                    // Remove any panels that don't belong in container
                    // In the contextual case panels are not removed because doing so is disorienting
                    container.getPanels().forEach(function (child) {
                        var properties = child.properties || child;
                        containedPanelIds.push(properties.panelId);
                        if (!isContextual && properties.setId === setId && (
                            !state[region] || !that._stateIncludes(region, state[region], properties.panelId))) {
                            child.originalRegion = region;
                            removedChildren.push(child);
                            container.removePanel(child, properties);
                        }
                    });
                    if (!state[region] && container.isEmpty()) {
                        // Remove the container
                        that._removePanelContainer(region);
                        container = null;
                    }
                }
                else if (state[region]) {
                    var stateChildren = state[region].children || state[region].panels;
                    if (stateChildren && stateChildren.length > 0) {
                        // Create and add a container
                        container = that._createPanelContainer(region);
                        that._addPanelContainer(container, region);
                    }
                }
                if (container && state[region]) {
                    container.setState(state[region], setId);
                }
            });
            if (removedChildren.length > 0) {
                // Add removed children to the panel container specified by the state
                leftOverChildren = [];
                removedChildren.forEach(function (child) {
                    var childAdded = false;
                    var properties = child.properties || child;
                    ["left", "right", "bottom"].forEach(function (region) {
                        if (state[region] && that._stateIncludes(region, state[region], properties.panelId)) {
                            that._addToPanelContainer(that._getPanelContainerInRegion(region), child);
                            properties.set(LayoutChildProperty.REGION, region);
                            childAdded = true;
                        }
                    });
                    if (!childAdded) {
                        leftOverChildren.push(child);
                    }
                });
                // Declare left over children "closed:
                leftOverChildren.forEach(function (child) {
                    var properties = child.properties || child;
                    properties.set(LayoutChildProperty.IS_OPEN, false);
                });
                removedChildren.forEach(function (child) {
                    delete child.originalRegion;
                });
            }
            if (setId) {
                // Restore border collapsed states
                contentBox  = domGeometry.getContentBox(this.domNode, this.computedStyle);
                ["left", "right", "bottom"].forEach(function (region) {
                    if (state[region] && state[region].border) {
                        if (region === "bottom") {
                            that._restoreBorderState(region, state[region].border, state.referenceHeight === contentBox.h, state.referenceHeight);
                        } else {
                            that._restoreBorderState(region, state[region].border, state.referenceWidth === contentBox.w, state.referenceWidth);
                        }
                    }
                });
            } else {
                this.inherited(arguments);
            }

            // Save the specified target regions for panels that aren't currently present
            ["left", "right", "bottom"].forEach(function (region) {
                if (state[region]) {
                    var stateChildren = state[region].children || state[region].panels;
                    if (stateChildren) {
                        stateChildren.forEach(function (child) {
                            var panelId = child.id || child.panelId;
                            if (containedPanelIds.indexOf(panelId) < 0) {
                                that._pendingPanelIdToRegionMap[panelId] = region;
                            }
                        });
                    }
                }
            });

            this._isInSetState = false;
        },

        /**
         * Overrides the inherited method to return true if the BorderContainer or any of its panel
         * containers is being restored
         *
         * @instance
         */
        isRestoringState: function () {
            var returnValue = false;
            if (this.inherited(arguments)) {
                returnValue = true;
            } else {
                var container;
                var that = this;
                ["left", "right", "bottom"].forEach(function (region) {
                    container = that._getPanelContainerInRegion(region);
                    if (container && container.isInstanceOf(Restorable) && container.isRestoringState()) {
                        returnValue = true;
                    }
                });
            }
            return returnValue;
        },

        /**
         * @instance
         */
        endStateRestore: function () {
            var that = this;
            ["left", "right", "bottom"].forEach(function (region) {
                var container = that._getPanelContainerInRegion(region);
                if (container) {
                    if (container.isEmpty()) {
                        that._removePanelContainer(region);
                    } else if (container.isRestoringState()) {
                        container.endStateRestore();
                    }
                }
            });
        },

        /**
         * Overrides the inherited method to ...
         *
         * @instance
         */
        revealChild: function (widget) {
            if (this.enableOnTopExpand && this.isCollapsed(widget.region)) {
                return;
            }
            this.inherited(arguments);
        },

        getOccupiedBorders: function () {
            var that = this;
            var occupied = [];
            ["left", "right", "bottom"].forEach(function (region) {
                if (that.getChildInRegion(region)) {
                    occupied.push(region);
                }
            });
            return occupied;
        },

        _addToBorder: function (widget, region) {
            // Put widget into a panel container (creating one if necessary).
            var container = this._getPanelContainerInRegion(region);
            if (!container) {
                container = this._createPanelContainer(region);
                this._addPanelContainer(container, region, widget);
            }
            this._addToPanelContainer(container, widget);
        },

        _addToPanelContainer: function (container, widget) {
            container.addPanel(widget, widget.properties);
            if (!this.isCollapsed(container.region)) {
                widget.properties.set(LayoutChildProperty.IS_SHOWING, true);
            }
        },

        _bindCenterProperties: function (properties) {
            this.inherited(arguments);
            var that = this;
            // Check for existence of watch rather than instanceof Stateful since the later check may fail if
            // properties are used across window contexts.
            if (properties.watch && properties.set && properties.get) {
                var collapseWatch = properties.watch(LayoutChildProperty.IS_COLLAPSED, function (name, oldValue, newValue) {
                    if (newValue) {
                        that._detectPanelMaximize();
                    } else {
                        // The center has been expanded, no panels should be considered maximized
                        ["left", "right", "bottom"].forEach(function (region) {
                            var container = that._getPanelContainerInRegion(region);
                            if (container) {
                                container.getPanelProperties().forEach(function (properties) {
                                    that._clearIsMaximized(properties);
                                });
                            }
                        });
                    }
                });
                var originalUnbind = this._unbindCenterProperties;
                this._unbindCenterProperties = function () {
                    if (originalUnbind) {
                        originalUnbind();
                    }
                    collapseWatch.remove();
                };
            }
        },

        _bindPanelProperties: function (widget, properties) {
            var that = this;
            // Check for existence of watch rather than instanceof Stateful since the later check may fail if
            // properties are used across window contexts.
            if (!widget._unbindPanelProperties && properties.watch && properties.set && properties.get) {
                widget.properties = properties;
                var watches = [];
                watches.push(properties.watch(LayoutChildProperty.IS_OPEN, function (name, oldValue, newValue) {
                    if (!newValue) {
                        widget._unbindPanelProperties();
                        // Save the final panel free dimension
                        if (properties.region) {
                            var dimension = that.getFreeDimension(properties.region);
                            if (properties.region === "left" || properties.region === "right") {
                                properties.__lastWidth = dimension;
                            } else {
                                properties.__lastHeight = dimension;
                            }
                        }
                    }
                }));
                watches.push(properties.watch(LayoutChildProperty.REGION, function (name, oldValue, newValue) {
                    if (that.isRestoringState()) {
                        return;
                    }
                    var panelContainer = that._getPanelContainerAncestor(widget);
                    if (panelContainer.region !== newValue) {
                        panelContainer.removePanel(widget, properties);
                        // Index in current region should not affect placement in new region
                        if (properties.index != undefined) {
                            delete properties.index;
                        }
                        that._addToBorder(widget, newValue);
                    }
                    // Following a move, none of the panels in the target region are maximized.
                    that._getPanelContainerInRegion(newValue).getPanelProperties().forEach(function (otherProperties) {
                        that._clearIsMaximized(otherProperties);
                    });
                }));
                watches.push(properties.watch(LayoutChildProperty.IS_COLLAPSED, function (name, oldValue, newValue) {
                    if (newValue) {
                        // If panel is collapsed it should no longer be considered maximized
                        that._clearIsMaximized(properties);
                        that._detectPanelMaximize();
                    } else {
                        // If a panel is restored others in same region can no longer be considered maximized
                        if (!that._panelDragger._originInfo || that._panelDragger._originInfo.properties !== properties) {
                            var container = that._getPanelContainerInRegion(properties.region);
                            if (container) {
                                container.getPanelProperties().forEach(function (otherProperties) {
                                    if (otherProperties !== properties) {
                                        that._clearIsMaximized(otherProperties);
                                    }
                                });
                            }
                        }
                    }
                }));
                watches.push(properties.watch(LayoutChildProperty.IS_MAXIMIZED, function (name, oldValue, newValue) {
                    if(!that._started) {
                        return;
                    }
                    if (newValue) {
                        // Upon maximize, if a panel in a different region is currently maximized then restore it before proceeding
                        // Also if hide any on-top panel
                        ["left", "right", "bottom"].forEach(function (otherRegion) {
                            if (properties.region !== otherRegion) {
                                var container = that._getPanelContainerInRegion(otherRegion);
                                if (container) {
                                    container.hideOnTopPanels();
                                    container.getPanelProperties().forEach(function (otherProperties) {
                                        if (otherProperties.get(LayoutChildProperty.IS_MAXIMIZED)) {
                                            var animationEnabled = LayoutUtils.isAnimationEnabled();
                                            LayoutUtils.disableLayoutAnimation();
                                            otherProperties.set(LayoutChildProperty.IS_MAXIMIZED, false);
                                            if (animationEnabled) {
                                                LayoutUtils.enableLayoutAnimation();
                                            }
                                        }
                                    });
                                }
                            }
                        });
                        MaximizingContainer.maximizeGlobally(widget);
                    } else if (!that._clearingMaximized) {
                        MaximizingContainer.restoreGlobally(widget);
                    }

                }));
                widget._unbindPanelProperties = function () {
                    watches.forEach(function (watch) {
                        watch.remove();
                    });
                    delete widget.properties;
                    delete widget._unbindPanelProperties;
                };
            }
            if (!this.supportFooter) {
                properties._globalActionsSupplier = {
                    getActions: function () {
                        actions = [];
                        var panelContainer = that._getPanelContainerAncestor(widget);
                        if (panelContainer) {
                            var region = panelContainer.region;
                            var isCollapsed = panelContainer.isContainerCollapsed();
                            var action = {
                                enabled: true,
                                callback: function () {
                                    that.set(COLLAPSED_PROPERTY[region], !isCollapsed);
                                }
                            };
                            if (region === "bottom") {
                                action.text = isCollapsed ? layoutL10n.expandBottomPanel : layoutL10n.collapseBottomPanel;
                            } else {
                                action.text = isCollapsed ? layoutL10n.expandSidePanel : layoutL10n.collapseSidePanel;
                            }
                            actions.push(action);
                        }
                        return actions;
                    }
                };
            }
        },

        _createPanelContainer: function (region) {
            var container = region === "bottom" ? this._createBottomContainer() : this._createSideContainer(region);
            this._panelDragger.attach(container);
            var that = this;
            container.on(LayoutContainerEvent.CHILD_ADDED, function (widget, properties) {
                if (container !== that.getChildInRegion(region)) {
                    that._addPanelContainer(container, region);
                }
                // Clear the maximized property associated with other panels already in the container
                container.getPanelProperties().forEach(function (otherProperties) {
                    if (otherProperties !== properties) {
                        that._clearIsMaximized(otherProperties);
                    }
                });
                that.emit(LayoutContainerEvent.CHILD_ADDED, widget, properties);
            });
            container.on(LayoutContainerEvent.CHILD_REMOVED, function (widget, properties) {
                if (!that._isInSetState && container.isEmpty()) {
                    that._removePanelContainer(region);
                }
                that.emit(LayoutContainerEvent.CHILD_REMOVED, widget, properties);
            });
            // Forward state changed events from the container
            container.on(LayoutContainerEvent.STATE_CHANGED, function () {
                that.emit(LayoutContainerEvent.STATE_CHANGED);
            });
            // Respond to collapse all and expand all
            if (this.enableOnTopExpand) {
                container.on(LayoutContainerEvent.EXPAND_ALL, function () {
                    that.set(COLLAPSED_PROPERTY[region], false);
                });
                container.on(LayoutContainerEvent.COLLAPSE_ALL, function () {
                    that.set(COLLAPSED_PROPERTY[region], true);
                });
            }
            return container;
        },

        _createSideContainer: function (region) {
            return new SideContainer({
                id : this.id + region + "SideContainer",
                region: region, enableOnTopExpand: this.enableOnTopExpand,
                panelSetTitles: this.panelSetTitles,
                animationEnabled: this.animationEnabled,
                borderOptions: this.borderOptions ? this.borderOptions[region] : null,
                reserveSpaceForTabs: !!this.supportFooter
            });
        },

        _createBottomContainer: function () {
            return new BottomContainer({
                id: this.id + "BottomContainer",
                hasNeighborAbove: this._hasCenterChild(),
                enableOnTopExpand: this.enableOnTopExpand,
                animationEnabled: this.animationEnabled,
                borderOptions: this.borderOptions ? this.borderOptions["bottom"] : null,
                supportFooter: this.supportFooter
            });
        },

        _getPanelContainerAncestor: function (widget) {
            var parent = widget.getParent();
            while (parent) {
                if (parent.isInstanceOf(PanelContainer)) {
                    return parent;
                }
                // Cover case of child expanded on top
                if (parent.parentBar) {
                    return this.getChildInRegion(parent.parentBar.region);
                }
                parent = parent.getParent();
            }
            return null;
        },

        _setIsLeftCollapsedAttr: function (isCollapsed) {
            this._setIsRegionCollapsed("left", isCollapsed);
        },

        _getIsLeftCollapsedAttr: function () {
            return this._getIsRegionCollapsed("left");
        },

        _setIsRightCollapsedAttr: function (isCollapsed) {
            this._setIsRegionCollapsed("right", isCollapsed);
        },

        _getIsRightCollapsedAttr: function () {
            return this._getIsRegionCollapsed("right");
        },

        _setIsBottomCollapsedAttr: function (isCollapsed) {
            this._setIsRegionCollapsed("bottom", isCollapsed);
        },

        _getIsBottomCollapsedAttr: function () {
            return this._getIsRegionCollapsed("bottom");
        },

        _setIsRegionCollapsed: function (region, isCollapsed) {
            var borderInfo = this._borderInfo[region];
            if (borderInfo && borderInfo.properties) {
                borderInfo.properties.set(LayoutChildProperty.IS_COLLAPSED, isCollapsed);
            }
        },

        _getIsRegionCollapsed: function (region) {
            var borderInfo = this._borderInfo[region];
            return borderInfo && borderInfo.properties ? borderInfo.properties.get(LayoutChildProperty.IS_COLLAPSED) : false;
        },

        _setLeftWidthAttr: function (width) {
            this._setRegionDimension("left", width);
        },

        _getLeftWidthAttr: function () {
            return this._getRegionDimension("left");
        },

        _setRightWidthAttr: function (width) {
            this._setRegionDimension("right", width);
        },

        _getRightWidthAttr: function () {
            return this._getRegionDimension("right");
        },

        _setBottomHeightAttr: function (height) {
            this._setRegionDimension("bottom", height);
        },

        _getBottomHeightAttr: function () {
            return this._getRegionDimension("bottom");
        },

        _setRegionDimension: function (region, dimension) {
            this._requestedDimension[region] = dimension;
            if (this.getChildInRegion(region)) {
                this.setFreeDimension(region, this._convertToAbsoluteDimension(region, dimension));
            }
        },

        _getRegionDimension: function (region) {
            return this.getChildInRegion(region) ? this.getFreeDimension(region) : this._requestedDimension[region];
        },

        _clearIsMaximized: function (properties) {
            if (properties.get(LayoutChildProperty.IS_MAXIMIZED)) {
                this._clearingMaximized = true;
                properties.set(LayoutChildProperty.IS_MAXIMIZED, false);
                this._clearingMaximized = false;
            }
        },

        _detectPanelMaximize: function () {
            var that = this;

            // Determine if there is a panel that should be considered maximized by virtue of everything else being collapsed
            if (!this.isCollapsed("center")) {
                return;
            }
            var nonCollapsedRegion, i;
            var regions = ["left", "right", "bottom"];
            for (i = 0; i < regions.length; i++) {
                if (!that.isCollapsed(regions[i])) {
                    if (nonCollapsedRegion) {
                        return;  // There is more than one non-collapsed region
                    } else {
                        nonCollapsedRegion = regions[i];
                    }
                }
            }
            if (nonCollapsedRegion) {
                // There is one non-collapsed region.  See if it contains a single non-collapsed panel
                if (nonCollapsedRegion === "bottom") {
                    // Get the showing panel in the bottom tab container
                    this._getPanelContainerInRegion(nonCollapsedRegion).getPanelProperties().forEach(function (properties) {
                        if (properties.get(LayoutChildProperty.IS_SHOWING)) {
                            nonCollapsedProperties = properties;
                        }
                    });
                } else {
                    var nonCollapsedProperties;
                    var panelProperties = this._getPanelContainerInRegion(nonCollapsedRegion).getPanelProperties();
                    for (i = 0; i < panelProperties.length; i++) {
                        if (!panelProperties[i].get(LayoutChildProperty.IS_COLLAPSED)) {
                            if (nonCollapsedProperties) {
                                return;  // There is more than one non-collapsed panel in the region
                            } else {
                                nonCollapsedProperties = panelProperties[i];
                            }
                        }
                    }
                }
                if (nonCollapsedProperties && !nonCollapsedProperties.get(LayoutChildProperty.IS_MAXIMIZED)) {
                    // Mark the panel as maximized without triggering any response
                    this._clearingMaximized = true;
                    nonCollapsedProperties.set(LayoutChildProperty.IS_MAXIMIZED, true);
                    var borderInfo = this._borderInfo[nonCollapsedRegion];
                    borderInfo.isExpanded = true;
                    borderInfo.regionsCollapsedUponExpand = null;
                    this._clearingMaximized = false;
                }
            }
        },

        _convertToAbsoluteDimension: function (region, dimension) {
            if (dimension < 1) {
                var contentBox = domGeometry.getContentBox(this.domNode, this.computedStyle);
                if (region === "left" || region === "right") {
                    if (contentBox.w > 0) {
                        dimension = Math.floor(dimension * contentBox.w);
                    }
                } else {
                    if (contentBox.h > 0) {
                        dimension = Math.floor(dimension * contentBox.h);
                    }
                }
            }
            return dimension;
        },

        _addPanelContainer: function (container, region, firstChild) {
            var that = this;

            // The bottom may be occupied by a footer rather than a bottom panel container
            // In this case remove the footer before creating a bottom panel container
            var footer;
            if (region === "bottom") {
                footer = this._removeFooterFromBottom();
                if (footer) {
                    container.setFooter(footer);
                }
            }

            var wasCollapsed = container.isContainerCollapsed();

            var containerProperties = new Stateful({
                region        : region,
                hasSplitter   : true,
                isResizable   : true,
                isCollapsible : true,
                isProportional: true,
                minSize       : 120
//                isCollapsed   : wasCollapsed
// TODO: Support initial collapsed property in place of collapse call below.
            });
            if (this.borderOptions && this.borderOptions[region]) {
                lang.mixin(containerProperties, this.borderOptions[region]);
            }
            var dimension = this._requestedDimension[region];
            if (!dimension && firstChild) {
                dimension = (region === "left" || region === "right") ?
                    (firstChild.properties.__lastWidth || firstChild.properties.preferredWidth) :
                    (firstChild.properties.__lastHeight || firstChild.properties.preferredHeight);
            }
            if (dimension && dimension > 0 && dimension < 1) {
                containerProperties.proportion = dimension;
            }
            // Forward property watches (done before add so that initial values reported)
            containerProperties.watch(LayoutChildProperty.IS_COLLAPSED, function (name, oldValue, newValue) {
                that.notifyStateWatchers(COLLAPSED_PROPERTY[region], oldValue, newValue);
                container.collapseChange(newValue);
                that._handleRegionCollapseChange(region, newValue);
            });
            containerProperties.watch("freeDimension", function (name, oldValue, newValue) {
                that.notifyStateWatchers(DIMENSION_PROPERTY[region], oldValue, newValue);
            });
            this._isInternalAdd = true;
            this.addWithProperties(container, containerProperties);
            this._isInternalAdd = false;
            if (dimension !== undefined) {
                if (firstChild && dimension <= 0) {
                    var size = Geometry.measure(firstChild.domNode, this.domNode);
                    dimension = (region === "left" || region === "right") ? size.w : side.h;
                }
                if (dimension >= 1) {
                    this.setFreeDimension(region, dimension);
                }
            }
            if (wasCollapsed) {
                this.collapse(region);
            }
        },

        _removePanelContainer: function (region) {
            var container = this._getPanelContainerInRegion(region);
            var footer;
            var that = this;
            if (container) {
                this._isInternalRemove = true;
                this.removeChild(container);
                this._isInternalRemove = false;

                // If the removed container is a bottom container with a footer add the footer back to the bottom
                if (region === "bottom") {
                    footer = container.getFooter();
                    if (footer) {
                        container.setFooter(null);
                        this._addFooterToBottom(footer);
                    }
                }

                // Don't destroy the container if a panel was just dragged out of it because it may be dropped back in.
                // The container will be destroyed by the panel dragger when the drag ends (see InternalPanelDragger._clearOriginInfo)
                if (!that._panelDragger._originInfo || that._panelDragger._originInfo.container !== container) {
                    this._destroyPanelContainer(container);
                }
            }
        },

        _destroyPanelContainer: function (container) {
            container.destroyRecursive();
            if (container.collapsedWidget) {
                container.collapsedWidget.destroyRecursive();
            }
        },

        hasPanelInRegion: function(region) {
            var container = this._getPanelContainerInRegion(region);
            return container && container.getPanelCount() > 0;
        },

        _getPanelContainerInRegion: function (region) {
            var container = this.getChildInRegion(region);
            return (container && container.isInstanceOf(PanelContainer)) ? container : null;
        },

        _hasCenterChild: function () {
            return !!this.getChildInRegion("center");
        },

        _handleRegionCollapseChange: function (region, isCollapsed) {
            var that = this;
            // If collapsed, clear the maximized state of any panels in region
            if (isCollapsed) {
                this._getPanelContainerInRegion(region).getPanelProperties().forEach(function (properties) {
                    that._clearIsMaximized(properties);
                });
                that._detectPanelMaximize();
            } else {
                // If expanded, clear the maximized state of any panels in other regions
                ["left", "right", "bottom"].forEach(function (otherRegion) {
                    if (region !== otherRegion) {
                        var container = that._getPanelContainerInRegion(otherRegion);
                        if (container) {
                            container.getPanelProperties().forEach(function (properties) {
                                that._clearIsMaximized(properties);
                            });
                        }
                    }
                });
            }
        },

        _updateBottomContainer: function () {
            var bottomContainer = this._getPanelContainerInRegion("bottom");
            if (bottomContainer) {
                bottomContainer.set("hasNeighborAbove", this._hasCenterChild());
            }
        },

        _addFooterToBottom: function (footer) {
            var footerContainer = new ContentPane({id: this.id + "FooterContainer"});
            domClass.add(footerContainer.domNode, "footerContainer");
            domStyle.set(footerContainer.domNode, {height: "22px"});  // TODO: Base size on footer
            footerContainer.addChild(footer);
            footerContainer.getFooter = function () {return footer;};
            this._isInternalAdd = true;
            this.addWithProperties(footerContainer, {
                region      : "bottom",
                hasSplitter : false,
                isResizable : false
            });
            this._isInternalAdd = false;
        },

        _removeFooterFromBottom: function () {
            var footerContainer = this.getChildInRegion("bottom");
            var footer;
            if (footerContainer) {
                this._isInternalRemove = true;
                this.removeChild(footerContainer);
                this._isInternalRemove = false;
                footer = footerContainer.getChildren()[0];
                footerContainer.removeChild(footer);
                footerContainer.destroyRecursive();
            }
            return footer;
        },

        _stateIncludes: function (region, state, panelId) {
            return region === "bottom" ? BottomContainer.stateIncludes(state, panelId)
                                       : SideContainer.stateIncludes(state, panelId);
        },

        _containsOnly: function (container, setId) {
            var panels = container.getPanels();
            var i;
            for (i = 0; i < panels.length; i++) {
                if (!panels[i].properties || panels[i].properties.setId !== setId) {
                    return false;
                }
            }
            return true;
        }
    });

    ArrangeableBorderContainer.IS_LEFT_COLLAPSED   = IS_LEFT_COLLAPSED;
    ArrangeableBorderContainer.IS_RIGHT_COLLAPSED  = IS_RIGHT_COLLAPSED;
    ArrangeableBorderContainer.IS_BOTTOM_COLLAPSED = IS_BOTTOM_COLLAPSED;
    ArrangeableBorderContainer.LEFT_WIDTH = LEFT_WIDTH;
    ArrangeableBorderContainer.RIGHT_WIDTH = RIGHT_WIDTH;
    ArrangeableBorderContainer.BOTTOM_HEIGHT = BOTTOM_HEIGHT;
    ArrangeableBorderContainer.REGION_IS_COLLAPSED = {
        left: IS_LEFT_COLLAPSED,
        right: IS_RIGHT_COLLAPSED,
        bottom: IS_BOTTOM_COLLAPSED
    };

    /**
     * Returns true if the given container state object includes the specified panel id
     */
    ArrangeableBorderContainer.stateIncludes = function (state, panelId) {
        return (state.left && SideContainer.stateIncludes(state.left, panelId)) ||
               (state.right && SideContainer.stateIncludes(state.right, panelId)) ||
               (state.bottom && BottomContainer.stateIncludes(state.bottom, panelId));
    };

    return ArrangeableBorderContainer;
});
